import dayjs from 'dayjs';
import throttle from 'lodash/throttle';
import { useCallback, useEffect, useRef, useState } from 'react';

const activityKey = 'tyro:last-activity';
const countDownSeconds = 60;

const onActivity = throttle(() => {
  localStorage.setItem(activityKey, String(dayjs().unix()));
}, 30000);

export function useIdleTracker(maxIdleMinutes: number, onTimeout: () => void) {
  const [isIdle, setIsIdle] = useState(false);
  const [countDown, setCountDown] = useState(countDownSeconds);
  const onTimeoutRef = useRef(onTimeout);
  const countDownTimerRef = useRef<NodeJS.Timeout>(undefined);

  onTimeoutRef.current = onTimeout;

  const clearCountDown = useCallback(() => {
    if (countDownTimerRef.current) {
      clearInterval(countDownTimerRef.current);
      countDownTimerRef.current = undefined;
    }
    setCountDown(countDownSeconds);
  }, []);

  const resetActivity = useCallback(() => {
    setIsIdle(false);
    clearCountDown();
    onActivity();
  }, [clearCountDown]);

  useEffect(() => {
    window.addEventListener('mousemove', onActivity);
    window.addEventListener('keydown', onActivity);
    window.addEventListener('scroll', onActivity);
    onActivity();

    const checker = setInterval(() => {
      const lastActivity = localStorage.getItem(activityKey);
      if (!lastActivity) return;

      const idleMinutes = dayjs().diff(dayjs.unix(Number(lastActivity)), 'm');
      if (idleMinutes >= maxIdleMinutes) {
        onTimeoutRef.current();
      } else if (idleMinutes >= maxIdleMinutes - 2) {
        setIsIdle(true);
      }
    }, 60000);

    return () => {
      window.removeEventListener('mousemove', onActivity);
      window.removeEventListener('keydown', onActivity);
      window.removeEventListener('scroll', onActivity);
      clearInterval(checker);
    };
  }, [maxIdleMinutes]);

  useEffect(() => {
    if (!isIdle) return;

    countDownTimerRef.current = setInterval(() => {
      setCountDown((prev) => {
        if (prev <= 1) {
          onTimeoutRef.current();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return clearCountDown;
  }, [isIdle, clearCountDown]);

  return { isIdle, countDown, resetActivity };
}
